import React, { FC, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { Loader, MyScore } from 'src/components';
import { FloatingMiniPlayer } from 'src/components/FloatingMiniPlayer';
import { useUserContext } from 'src/context/user';
import { MeApi } from 'src/api';
import { ActivitySource } from 'src/utils/analytics/analytics.const';
import {
  PointsResponse,
  ProgressResponse,
  RecommendationsActivityType,
  RecommendationsResponse,
} from 'src/api/me/types';
import { useHomeModals } from 'src/apps/Home/Overview/useHomeModals';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';
import {
  hasInLocalStorageArrayEntry,
  addToLocalStorageArrayEntry,
} from 'src/utils/webStorageArrayUtils';
import { AI_NOTIFICATION, AI_NOTIFICATION_BANNER_DISMISSED } from 'src/constants/ai.const';
import { Activities } from 'src/components/MiniPlayer';
import { MyActivitiesDashboardCard } from 'src/apps/Activities/MyActivitiesDashboardCard';
import { BuddiesPromotionCard } from 'src/apps/Buddies/BuddiesPromotionCard';
import { MemriseConnectPromoCard } from 'src/apps/Experiments/MemriseConnectPromoCard';
import { SOURCE_LANGUAGE, TARGET_LANGUAGE } from 'src/utils/language/saveLastLanguagePair';
import { CONNECT_PROMO_DISMISSED_LPS_STORAGE_KEY } from 'src/apps/Experiments/constants';
import * as S from './styles';

export type OverviewProps = {
  languagePairId?: number;
  isSupportedLanguagePairCommunicate?: boolean;
  isSupportedLanguagePairImmerse?: boolean;
  isSupportedLanguagePairBuddies?: boolean;
  targetLanguageName?: string;
};

export const Overview: FC<OverviewProps> = ({
  languagePairId,
  isSupportedLanguagePairCommunicate,
  isSupportedLanguagePairImmerse,
  isSupportedLanguagePairBuddies,
  targetLanguageName,
}) => {
  const { user } = useUserContext();
  const [activity, setActivity] = useState<RecommendationsActivityType>();
  const [, setRerenderKey] = useState(uuidv4());
  const [aiBannerDismissed, setAiBannerDismissed] = useState(
    !!getLocalStorageEntry(AI_NOTIFICATION_BANNER_DISMISSED),
  );
  const buddyTabNotificationDate = getLocalStorageEntry(AI_NOTIFICATION);
  const shouldDisplayAiBanner = useMemo(() => {
    return (
      isSupportedLanguagePairBuddies &&
      !aiBannerDismissed &&
      !buddyTabNotificationDate &&
      user &&
      user.statistics.wordsLearnt > 0
    );
  }, [isSupportedLanguagePairBuddies, aiBannerDismissed, buddyTabNotificationDate, user]);

  const sourceLanguage = getLocalStorageEntry(SOURCE_LANGUAGE) ?? '';
  const targetLanguage = getLocalStorageEntry(TARGET_LANGUAGE) ?? '';
  const isEnglishSource = ['en', 'en-us'].includes(sourceLanguage);
  const currentLPKey = `${sourceLanguage}:${targetLanguage}`;
  const isCurrentLPDismissed = hasInLocalStorageArrayEntry(
    CONNECT_PROMO_DISMISSED_LPS_STORAGE_KEY,
    currentLPKey,
  );

  const { isLanguageSupported, shouldDisplayMemriseConnectBanner } = useMemo(() => {
    const supportedLanguages = ['de', 'es', 'es-mx', 'fr', 'jp-full', 'pt', 'zh-cn'];
    const isSupported = isEnglishSource && supportedLanguages.includes(targetLanguage);
    return {
      isLanguageSupported: isSupported,
      shouldDisplayMemriseConnectBanner: !isCurrentLPDismissed && isSupported,
    };
  }, [isEnglishSource, targetLanguage, isCurrentLPDismissed]);

  const { data: recommendations, isLoading: isLoadingRecommendations } =
    useQuery<RecommendationsResponse | null>({
      queryKey: ['getRecommendations', languagePairId],
      queryFn: () => {
        /* istanbul ignore if: should be defined */
        if (!languagePairId) return null;
        return MeApi.getRecommendations(languagePairId);
      },
    });

  const { data: points, isLoading: isLoadingPoints } = useQuery<PointsResponse | null>({
    queryKey: ['getPoints', languagePairId],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return null;
      return MeApi.getPoints(languagePairId);
    },
  });

  const { data: progress, isLoading: isLoadingProgress } = useQuery<ProgressResponse | null>({
    queryKey: ['getProgress', languagePairId],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!languagePairId) return null;
      return MeApi.getProgress(languagePairId);
    },
  });

  useEffect(() => {
    recommendations && setActivity(recommendations.recommendationOrdering[0]);
  }, [recommendations]);

  const isLoadingSupportedActivities =
    typeof isSupportedLanguagePairImmerse !== 'boolean' ||
    typeof isSupportedLanguagePairCommunicate !== 'boolean';

  const supportedActivities = useMemo(
    () =>
      [
        'learn',
        ...(isSupportedLanguagePairImmerse ? ['immerse'] : []),
        ...(isSupportedLanguagePairCommunicate ? ['communicate'] : []),
      ] as Activities[],
    [isSupportedLanguagePairImmerse, isSupportedLanguagePairCommunicate],
  );

  const homeModals = useHomeModals({
    languagePairId,
    supportedActivities: isLoadingSupportedActivities ? null : supportedActivities,
  });

  const handleCloseBanner = () => {
    setLocalStorageEntry(AI_NOTIFICATION_BANNER_DISMISSED, 'true');
    setAiBannerDismissed(true);
  };

  const handleCloseConnectPromo = () => {
    addToLocalStorageArrayEntry(CONNECT_PROMO_DISMISSED_LPS_STORAGE_KEY, currentLPKey);
    setRerenderKey(uuidv4());
  };

  if (
    isLoadingPoints ||
    isLoadingProgress ||
    isLoadingRecommendations ||
    isLoadingSupportedActivities
  ) {
    return (
      <S.LoaderContainer>
        <Loader />
      </S.LoaderContainer>
    );
  }

  return (
    <>
      {homeModals}
      {points && (
        <>
          {targetLanguageName && (
            <S.ProgressLevel
              stage={points.stage.stageName}
              languageName={targetLanguageName}
              currentLevel={points.level.currentLevel}
              minimumPoints={points.level.minimumPoints}
              maximumPoints={points.level.maximumPoints}
              totalPoints={points.points.totalPoints}
            />
          )}
          {shouldDisplayMemriseConnectBanner && (
            <S.BuddiesPromotionCardMobile>
              <MemriseConnectPromoCard onClose={handleCloseConnectPromo} />
            </S.BuddiesPromotionCardMobile>
          )}
          {shouldDisplayAiBanner && !isLanguageSupported && (
            <S.BuddiesPromotionCardMobile>
              <BuddiesPromotionCard onClose={handleCloseBanner} />
            </S.BuddiesPromotionCardMobile>
          )}
          {progress && (
            <MyScore
              collapsible
              selectedKey={activity}
              onClick={setActivity}
              progress={progress}
              userPoints={points.points}
              supportedActivities={supportedActivities}
            />
          )}
          <S.CardsWrapper>
            {shouldDisplayMemriseConnectBanner && (
              <S.BuddiesPromotionCardDesktop>
                <MemriseConnectPromoCard onClose={handleCloseConnectPromo} />
              </S.BuddiesPromotionCardDesktop>
            )}
            {shouldDisplayAiBanner && !isLanguageSupported && (
              <S.BuddiesPromotionCardDesktop>
                <BuddiesPromotionCard onClose={handleCloseBanner} />
              </S.BuddiesPromotionCardDesktop>
            )}
            <MyActivitiesDashboardCard />
          </S.CardsWrapper>
        </>
      )}
      <FloatingMiniPlayer
        currentActivity={activity}
        onChangeActivity={setActivity}
        recommendations={recommendations}
        supportedActivities={supportedActivities}
        source={ActivitySource.HOME_TAB}
      />
    </>
  );
};
